/**
 * React auth context + `useAuth()` hook.
 *
 * Loads `/api/auth/me` and `/api/auth/providers` once on mount and exposes:
 *   - user          — `AuthUser | null`  (null = anonymous)
 *   - entitlements  — tier limits, always present (anonymous tier when signed out)
 *   - mode          — "off" | "optional" | "required"
 *   - providers     — configured OAuth providers
 *   - refresh()     — re-fetch /api/auth/me (use after upgrades, sign-outs)
 *   - signOut()     — POST /api/auth/logout then refresh
 *   - ready         — true once the first load has settled (success OR error)
 *
 * If the API returns 404 for /api/auth/me (pre-auth servers) we quietly
 * disable the auth UI so the dashboard still works against older backends.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  getAuthMe,
  getAuthProviders,
  postLogout,
  type AuthMeResponse,
  type AuthMode,
  type AuthProvidersResponse,
  type AuthUser,
  type Entitlements,
  type SubscriptionSummary,
  type UsageSummary,
} from "./api";

interface AuthContextValue {
  /** null = anonymous. Becomes a user object after sign-in. */
  user: AuthUser | null;
  /** Always present — anonymous entitlements when signed out. */
  entitlements: Entitlements;
  /** Phase 2 — populated after a Stripe customer exists. null pre-checkout. */
  subscription: SubscriptionSummary | null;
  /** Phase 2 — monthly usage (used/included/overage). null on admin/enterprise. */
  usage: UsageSummary | null;
  mode: AuthMode;
  providers: AuthProvidersResponse["providers"];
  /** false = we haven't heard back from /api/auth/me yet. Used by route guards. */
  ready: boolean;
  /** true when the API doesn't even expose /api/auth/*. Hides the auth UI. */
  disabled: boolean;
  /**
   * Server advertises whether the dev-plan override (UAIP_DEV_PLAN_OVERRIDE=1)
   * is on. Default false — the Account-page dev switcher keys off this.
   */
  devPlanOverrideEnabled: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const ANON_ENTITLEMENTS: Entitlements = {
  plan: "anonymous",
  crawlsPerDay: 3,
  crawlsPerMonth: 0,
  maxPagesPerCrawl: 5,
  maxTargetHosts: 0,
  allowSemantic: false,
  allowHud: false,
  allowAuthedCrawl: false,
  // Anonymous visitors can only pick the Free-tier emitter (playwright-ts).
  // Mirrors `FREE_FRAMEWORKS` in `apps/api/src/billing/tiers.ts`. Once /api/auth/me
  // responds, this gets replaced with the authoritative server-side list.
  allowedEmits: ["playwright-ts"],
  // Anonymous can't create projects at all; leave projectCount undefined —
  // the dashboard's quota card only renders for authed users anyway.
  maxProjects: 0,
};

const defaultValue: AuthContextValue = {
  user: null,
  entitlements: ANON_ENTITLEMENTS,
  subscription: null,
  usage: null,
  mode: "off",
  providers: [],
  ready: false,
  disabled: false,
  devPlanOverrideEnabled: false,
  refresh: async () => undefined,
  signOut: async () => undefined,
};

const AuthContext = createContext<AuthContextValue>(defaultValue);

export function AuthProvider(props: { children: React.ReactNode }): JSX.Element {
  const [me, setMe] = useState<AuthMeResponse | null>(null);
  const [providersResp, setProvidersResp] = useState<AuthProvidersResponse | null>(null);
  const [ready, setReady] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const loadMe = useCallback(async () => {
    try {
      const res = await getAuthMe();
      if (res === null) {
        // API has no /api/auth routes — treat whole feature as off.
        setDisabled(true);
        setMe(null);
      } else {
        setMe(res);
      }
    } catch (err) {
      // Network / 5xx — leave as anonymous but don't hide the UI. A transient
      // API blip shouldn't permanently hide sign-in.
      console.warn("auth: /api/auth/me failed", err);
      setMe(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await Promise.all([
        loadMe(),
        (async () => {
          try {
            const p = await getAuthProviders();
            if (!cancelled) setProvidersResp(p);
          } catch (err) {
            console.warn("auth: /api/auth/providers failed", err);
          }
        })(),
      ]);
      if (!cancelled) setReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [loadMe]);

  const refresh = useCallback(async () => {
    await loadMe();
  }, [loadMe]);

  const signOut = useCallback(async () => {
    try {
      await postLogout();
    } catch (err) {
      // Log but proceed — we still want to clear local state so the UI
      // doesn't look stuck in an authed state if the server already purged
      // the session.
      console.warn("auth: logout failed", err);
    }
    await loadMe();
  }, [loadMe]);

  const value = useMemo<AuthContextValue>(() => {
    // Entitlements: prefer server-reported (so plan upgrades take effect
    // immediately on refresh), fall back to anon defaults.
    const entitlements = me?.entitlements ?? ANON_ENTITLEMENTS;
    const mode: AuthMode = providersResp?.mode ?? (disabled ? "off" : "optional");
    const providers = providersResp?.providers ?? [];
    return {
      user: me?.user ?? null,
      entitlements,
      subscription: me?.subscription ?? null,
      usage: me?.usage ?? null,
      mode,
      providers,
      ready,
      disabled,
      devPlanOverrideEnabled: me?.devPlanOverrideEnabled === true,
      refresh,
      signOut,
    };
  }, [me, providersResp, ready, disabled, refresh, signOut]);

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/**
 * Short human-readable label for a plan. Used in badges.
 */
export function planLabel(plan: Entitlements["plan"]): string {
  switch (plan) {
    case "anonymous":
      return "Guest";
    case "free":
      return "Free";
    case "starter":
      return "Starter";
    case "pro":
      return "Pro";
    case "team":
      return "Team";
    case "enterprise":
      return "Enterprise";
    case "admin":
      return "Admin";
  }
}

/**
 * Tailwind classes for a plan badge. Kept here so the TopNav and Account
 * page stay visually consistent without importing each other's styles.
 */
export function planBadgeClass(plan: Entitlements["plan"]): string {
  switch (plan) {
    case "anonymous":
      return "bg-slate-800 text-slate-300 border border-slate-700";
    case "free":
      return "bg-slate-800 text-slate-200 border border-slate-700";
    case "starter":
      return "bg-sky-900/50 text-sky-200 border border-sky-700/70";
    case "pro":
      return "bg-indigo-900/50 text-indigo-200 border border-indigo-700/70";
    case "team":
      return "bg-emerald-900/50 text-emerald-200 border border-emerald-700/70";
    case "enterprise":
      return "bg-fuchsia-900/50 text-fuchsia-200 border border-fuchsia-700/70";
    case "admin":
      return "bg-amber-900/40 text-amber-200 border border-amber-700/70";
  }
}
